/**
 * Sign-in (FD §3.1): the shared password, then "What's your name?" the first time. Both go to the
 * server in one POST /api/login. A browser that signed in before remembers the name, so later
 * sign-ins only ask for the password ("Not Sam?" switches person). After signing in, App moves on
 * to the page in `?next=` (or My plans).
 */
import { useState, type FormEvent } from 'react';
import { ApiFailure } from '../../api';
import { forgetName, rememberedName, signIn } from '../../store/session';

interface Props {
  /** Why sign-in is shown, e.g. "Please sign in again." after the sign-in ran out. */
  reason?: string;
}

/** The sign-in page. */
export function SignIn({ reason }: Props) {
  const [known, setKnown] = useState(rememberedName);
  const [step, setStep] = useState<'password' | 'name'>('password');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const send = async (who: string) => {
    setBusy(true);
    setError('');
    try {
      await signIn(password, who);
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiFailure && e.code === 'unauthorized') {
        setStep('password');
        setPassword('');
        setError("That password isn't right. Please try again.");
      } else if (e instanceof ApiFailure && e.code === 'rate_limited') {
        setStep('password');
        setError(e.message);
      } else {
        setError(e instanceof ApiFailure ? e.message : 'Something went wrong. Please try again.');
      }
    }
  };

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    if (!password) return;
    if (known) void send(known);
    else {
      setError('');
      setStep('name');
    }
  };

  const submitName = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) void send(name);
  };

  return (
    <main className="signin">
      <div className="signin-card">
        <div className="signin-logo" aria-hidden="true">🦒</div>
        <h1>Zoo Planner</h1>
        {step === 'password' ? (
          <form onSubmit={submitPassword}>
            {known ? (
              <p className="lead">
                Welcome back, <strong>{known}</strong>!{' '}
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    forgetName();
                    setKnown('');
                  }}
                >
                  Not {known}?
                </button>
              </p>
            ) : (
              <p className="lead">Plan your Planet Zoo 2 zoo together.</p>
            )}
            {reason && !error && <p className="note">{reason}</p>}
            <label className="field">
              <span>Password</span>
              <input
                type="password"
                autoFocus
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            {error && <p className="error" role="alert">{error}</p>}
            <button type="submit" className="primary big" disabled={busy || !password}>
              {known ? (busy ? 'Signing in…' : 'Sign in') : 'Next'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitName}>
            <p className="lead">What's your name?</p>
            <p className="hint">So you can both see who changed what. You only need to tell us once on this computer.</p>
            <label className="field">
              <span>Your name</span>
              <input autoFocus autoComplete="nickname" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            {error && <p className="error" role="alert">{error}</p>}
            <div className="row">
              <button type="button" onClick={() => setStep('password')} disabled={busy}>Back</button>
              <button type="submit" className="primary big grow" disabled={busy || !name.trim()}>
                {busy ? 'Signing in…' : "Let's go"}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
}
